import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { setSelectedMenuItem } from "@/components/Menu/menuSlice";
import { pointLayer } from '@/components/PQMap/pqMapSlice';
import { COUNTY_APN_FORMATS, countyApnFormat } from '@/lib/counties';
import type { SearchHistoryItem, SearchMode } from "@/types";
import { ActionIcon, Box, Divider, Drawer, Group, InputBase, Loader, Radio, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconChevronLeft, IconRefresh, IconSearch } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { IMaskInput } from 'react-imask';
import RecentSearches from "./components/RecentSearches";
import SearchResults from "./components/SearchResults";
import { currentSearchResults, getSearchResults, isSearching, searchError, setSearchResults } from './searchSlice';

const SEARCH_MODES: { value: SearchMode; label: string }[] = [
  { value: 'address', label: 'Address' },
  { value: 'owner', label: 'Owner' },
  { value: 'apn', label: 'APN' },
];

const Search: React.FC = () => {
  const dispatch = useAppDispatch();
  const [sideNavOpened, sideNavHandlers] = useDisclosure(false);
  const [county, setCounty] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounced] = useDebouncedValue(search, 750);
  const [mode, setMode] = useState<SearchMode>('address');
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const searching = useAppSelector(isSearching)
  const searchResults = useAppSelector(currentSearchResults);
  const currentPointLayer = useAppSelector(pointLayer);
  const error = useAppSelector(searchError);

  const openSearchHandler = () => {
    dispatch(setSelectedMenuItem('closed'));
    sideNavHandlers.toggle();
  }

  const searchHistorySelectedHandler = (search: SearchHistoryItem) => {
    resetSearch();
    setCounty(search.county.replace(' County', ''));
    setMode(search.mode);
    setSearch(search.search);
  }

  const searchResultSelectedHandler = () => {
    resetSearch();
    sideNavHandlers.toggle();
  }
  const countyUpdatedHandler = (value: string) => {
    setCounty(value);
    dispatch(setSearchResults([]));
    setSearch('');
  }
  const modeUpdatedHandler = (value: string) => {
    setMode(value as SearchMode);
    dispatch(setSearchResults([]));
    setSearch('');
  }
  const closeHandler = () => {
    resetSearch();
    sideNavHandlers.toggle();
  }

  const resetSearch = () => {
    setCounty(null);
    setMode('address');
    setSearch('');
    dispatch(setSearchResults([]));
  }

  useEffect(() => {
    dispatch(setSearchResults([]));
    if (!sideNavOpened || search !== searchDebounced || !county || !currentPointLayer || searchDebounced.trim().length < 3) {
      return;
    }
    const request = dispatch(getSearchResults({ search: searchDebounced, county, mode, pointLayer: currentPointLayer }));
    return () => { request.abort(); };
  }, [county, currentPointLayer, dispatch, mode, search, searchDebounced, sideNavOpened]);

  const searchInputProps = {
    value: search,
    onFocus: () => setSearchFocused(true),
    onBlur: () => setSearchFocused(false),
    rightSection: searching && <Loader size={20} color='pq-red' />,
  };
  const apnFormat = county ? countyApnFormat(county) : undefined;

  return (
    <>
      <ActionIcon variant='transparent' onClick={openSearchHandler} size='md' aria-label='Open Search'>
        <IconSearch color='white' />
      </ActionIcon>
      <Drawer
        opened={sideNavOpened}
        onClose={sideNavHandlers.toggle}
        withCloseButton={false}
        position='right'
        styles={{ body: { height: '100%' } }}
        size='100%'
        autoFocus={false}
        trapFocus={false}
      >
        <Stack style={{ height: '100%' }} gap={0}>
          <Box>
            <Group align='center' justify='center' style={{ padding: '0' }}>
              <ActionIcon style={{ position: 'absolute', top: 15, left: 20 }} onClick={closeHandler} variant='transparent' size='md' aria-label='Close Search'>
                <IconChevronLeft color='black' />
              </ActionIcon>
              <Title mb={10} order={4}>Search</Title>
              <ActionIcon style={{ position: 'absolute', top: 15, right: 20 }} onClick={resetSearch} variant='transparent' size='md' aria-label='Reset Search'>
                <IconRefresh color='black' />
              </ActionIcon>
            </Group>
            <Divider my={0} />
            <Stack mx="md" gap={5}>
              <Select
                label="Select a County"
                placeholder="Select a County"
                value={county}
                onChange={(value) => countyUpdatedHandler(value ?? '')}
                data={COUNTY_APN_FORMATS.map((entry) => entry.name)}
              />
              {county && mode === 'apn' && (
                <>
                  <InputBase
                    {...searchInputProps}
                    placeholder="Search APN"
                    // IMaskInput's union prop types don't compose with InputBase's polymorphic props.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    component={IMaskInput as any}
                    mask={apnFormat?.mask}
                    // 'A' accepts a letter or space (e.g. Alameda's book suffix).
                    definitions={{ 'A': /[a-zA-Z\s]/ }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)}
                  />
                  <Text size="xs">{`APN Format: ${apnFormat?.format}`}</Text>
                </>
              )}
              {county && mode !== 'apn' && (
                <>
                  <TextInput
                    {...searchInputProps}
                    placeholder={`Search ${mode === 'address' ? 'Address' : 'Owner'}`}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                  <Text size="xs">&nbsp;</Text>
                </>
              )}
              {county && (
                <Radio.Group size="xs" name="search-mode" value={mode} onChange={modeUpdatedHandler}>
                  <Group>
                    <Text size="sm">Search By:</Text>
                    {SEARCH_MODES.map(({ value, label }) => <Radio key={value} value={value} label={label} />)}
                  </Group>
                </Radio.Group>
              )}
            </Stack>
          </Box>
          {error && <Text role="alert" c="red" mx="md" my="sm">{error}</Text>}
          {!searchFocused && !searching && searchResults.length === 0 && (
            <RecentSearches onSearchHistorySelected={searchHistorySelectedHandler} />
          )}
          {searchResults.length > 0 && !searching && (
            <SearchResults onSelectSearchResult={searchResultSelectedHandler} />
          )}
          {searching && (
            <Stack h="100%" justify='center' align='center' gap={5}>
              <Loader size={100} color="pq-red" />
            </Stack>
          )}
        </Stack>
      </Drawer>
    </>
  );
};

export default Search;
